import { motion } from 'framer-motion';
import { cn, prefersReducedMotion } from '../../lib/utils';

export interface PlanStep {
  number: number;
  name: string;
  tools?: string;
  estimatedTokens?: number;
  reversible?: boolean;
}

export interface PlanCardProps {
  steps: PlanStep[];
  onApprove?: () => void;
  onEdit?: () => void;
  onCancel?: () => void;
  className?: string;
}

/**
 * Plan-mode review card. Each step appears with stagger animation;
 * whole card collapses to instant appear under prefers-reduced-motion.
 */
export function PlanCard({ steps, onApprove, onEdit, onCancel, className }: PlanCardProps) {
  const reduced = prefersReducedMotion();

  const containerVariants = reduced
    ? { hidden: {}, show: {} }
    : {
        hidden: { opacity: 1 },
        show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
      };

  const itemVariants = reduced
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 8 },
        show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } },
      };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className={cn('rounded-xl border border-brand-200 bg-bg-surface p-4', className)}
    >
      <div className="flex flex-col">
        {steps.map((step) => (
          <motion.div
            key={step.number}
            variants={itemVariants}
            className="flex gap-3 border-b border-dashed border-border-subtle py-2.5 last:border-b-0"
          >
            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-500 text-xs font-semibold text-white">
              {step.number}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-text-primary">{step.name}</div>
              {step.tools && (
                <div className="mt-0.5 font-mono text-xs text-text-tertiary">
                  tools: {step.tools}
                  {step.estimatedTokens && ` · est. ${step.estimatedTokens.toLocaleString()} tok`}
                  {step.reversible !== undefined && (
                    <> · {step.reversible ? 'reversible ✓' : 'irreversible'}</>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>
      {(onApprove || onEdit || onCancel) && (
        <div className="mt-4 flex justify-end gap-2 border-t border-border-subtle pt-3">
          {onCancel && (
            <button
              onClick={onCancel}
              className="rounded-md px-3 py-1.5 text-sm text-text-secondary transition-colors duration-fast hover:bg-bg-hover"
            >
              Cancel
            </button>
          )}
          {onEdit && (
            <button
              onClick={onEdit}
              className="rounded-md border border-brand-200 bg-bg-surface px-3 py-1.5 text-sm text-text-primary transition-colors duration-fast hover:bg-bg-hover"
            >
              Edit plan
            </button>
          )}
          {onApprove && (
            <button
              onClick={onApprove}
              className="rounded-md bg-brand-500 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-fast hover:bg-brand-600"
            >
              Approve &amp; execute →
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
}
